import { useState, useCallback } from 'react';
import { parseValidationErrors } from '@/utils/validationErrors';

const useFormValidation = (options = {}) => {
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldErrorMessages, setFieldErrorMessages] = useState({});
  const [generalErrors, setGeneralErrors] = useState([]);

  // INFO: Destructured so the callbacks below depend on the stable function
  // reference rather than the options object, which is new on every render.
  const { normalizePath } = options;

  const setBackendErrors = useCallback(
    (errorResponse) => {
      const parsed = parseValidationErrors(errorResponse, { normalizePath });

      setFieldErrors(parsed.fieldErrors);
      setFieldErrorMessages(parsed.fieldErrorMessages);
      setGeneralErrors(parsed.generalErrors);
    },
    [normalizePath],
  );

  const clearFieldError = useCallback((fieldName) => {
    setFieldErrors((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }

      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });

    setFieldErrorMessages((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }

      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setFieldErrors({});
    setFieldErrorMessages({});
    setGeneralErrors([]);
  }, []);

  const setClientFieldErrors = useCallback((errors) => {
    setFieldErrors((prev) => ({ ...prev, ...errors }));
    setFieldErrorMessages((prev) => {
      const updated = { ...prev };
      Object.entries(errors).forEach(([fieldName, message]) => {
        updated[fieldName] = Array.isArray(message) ? message : [message];
      });
      return updated;
    });
  }, []);

  const clearGeneralErrors = useCallback(() => {
    setGeneralErrors([]);
  }, []);

  const hasErrors = useCallback(() => {
    return Object.keys(fieldErrors).length > 0 || generalErrors.length > 0;
  }, [fieldErrors, generalErrors]);

  const hasFieldError = useCallback(
    (fieldName) => {
      return !!fieldErrors[fieldName];
    },
    [fieldErrors],
  );

  const getFieldErrors = useCallback(
    (fieldName) => {
      return fieldErrorMessages[fieldName] || [];
    },
    [fieldErrorMessages],
  );

  return {
    fieldErrors,
    fieldErrorMessages,
    generalErrors,
    setBackendErrors,
    setClientFieldErrors,
    clearFieldError,
    clearAllErrors,
    clearGeneralErrors,
    hasErrors,
    hasFieldError,
    getFieldErrors,
  };
};

export default useFormValidation;
